import { Fragment } from 'react';
import { Row } from '@tanstack/react-table';
import { groupBy } from 'lodash';
import { slugify } from 'markdown-to-jsx';

import { Checkbox } from '@@/form-components/Checkbox';

import { DefaultType } from './types';
import { TableContentOneColumn } from './TableContent';

interface Props<T extends DefaultType = DefaultType> {
  isLoading?: boolean;
  rows: Row<T>[];
  emptyContent?: string;
  renderRow(row: Row<T>): React.ReactNode;
  getGroupKey(row: Row<T>): string;
  isSelectionEnabled: boolean;
}

export function TableGroupedContent<T extends DefaultType = DefaultType>({
  isLoading = false,
  rows,
  emptyContent = 'No items available.',
  renderRow,
  getGroupKey,
  isSelectionEnabled,
}: Props<T>) {
  if (isLoading) {
    return <TableContentOneColumn>Loading...</TableContentOneColumn>;
  }

  if (!rows.length) {
    return <TableContentOneColumn>{emptyContent}</TableContentOneColumn>;
  }

  const groupedRows = groupBy(rows, getGroupKey);

  return (
    <>
      {Object.entries(groupedRows).map(([groupKey, group]) => {
        const selectableRows = group.filter((row) => row.getCanSelect());
        const allSelected = selectableRows.every((row) => row.getIsSelected());
        const someSelected = selectableRows.some((row) => row.getIsSelected());
        const groupKeySlug = slugify(groupKey);

        return (
          <Fragment key={`group-${groupKeySlug}`}>
            <tr>
              <th
                colSpan={Number.MAX_SAFE_INTEGER}
                className="!bg-gray-1 text-sm font-semibold th-highcontrast:!bg-black th-highcontrast:text-white th-dark:!bg-gray-iron-10 th-dark:text-white"
              >
                <div className="flex items-center gap-4 leading-10">
                  {isSelectionEnabled ? (
                    <>
                      <Checkbox
                        id={`select-group-${groupKeySlug}`}
                        data-cy={`datatable-group-${groupKeySlug}-select`}
                        checked={allSelected}
                        indeterminate={!allSelected && someSelected}
                        onChange={() => {
                          selectableRows.forEach((row) =>
                            row.toggleSelected(!allSelected)
                          );
                        }}
                        disabled={selectableRows.length === 0}
                        onClick={(e) => e.stopPropagation()}
                        aria-label={`Select all rows in ${groupKey}`}
                      />
                      <label
                        htmlFor={`select-group-${groupKeySlug}`}
                        className="!mb-0"
                      >
                        {groupKey}
                      </label>
                    </>
                  ) : (
                    <>{groupKey}</>
                  )}
                </div>
              </th>
            </tr>
            {group.map((row) => (
              <Fragment key={`row-${groupKeySlug}-${row.id}`}>
                {renderRow(row)}
              </Fragment>
            ))}
          </Fragment>
        );
      })}
    </>
  );
}
